import { AuthInterceptor } from './services/auth.interceptor';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorHandler, NgModule } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as Sentry from "@sentry/angular-ivy";

import { MomentModule } from 'ngx-moment';

import { ConfirmationService, MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { MessagesModule } from 'primeng/messages';
import { MessageModule } from 'primeng/message';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DropdownModule } from 'primeng/dropdown';
import { SliderModule } from 'primeng/slider';
import { DialogModule } from 'primeng/dialog';
import { TimelineModule } from 'primeng/timeline';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { MenuModule } from 'primeng/menu';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { TranslocoRootModule } from './transloco/transloco-root.module';
import { CDK_DRAG_CONFIG, DragDropModule } from '@angular/cdk/drag-drop';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ElementComponent } from './partials/element/element.component';
import { StartComponent } from './pages/start/start.component';
import { ContentContainerComponent } from './partials/content/content-container/content-container.component';
import { HeadlineComponent } from './partials/headline/headline.component';
import { LoginComponent } from './pages/login/login.component';
import { AcComponent } from './pages/ac/ac.component';
import { DashboardComponent } from './pages/ac/dashboard/dashboard.component';
import { NotfoundComponent } from './partials/notfound/notfound.component';
import { ByeComponent } from './pages/bye/bye.component';
import { NoticesComponent } from './pages/ac/notices/notices.component';
import { ExerciseComponent } from './pages/ac/exercise/exercise.component';
import { PreparationComponent } from './pages/ac/preparation/preparation.component';
import { ConferenceComponent } from './pages/ac/conference/conference.component';

import { CoreModule } from 'projects/core/src/lib/core.module';
import { RatingComponent } from './pages/ac/rating/rating.component';
import { FileListComponent } from './partials/file-list/file-list.component';
import { BackButtonComponent } from './partials/back-button/back-button.component';
import { ReportNumberPipe } from './pipes/report-number.pipe';
import { OnlinetestComponent } from './pages/ac/onlinetest/onlinetest.component';
import { OnlinetestNavComponent } from './pages/ac/onlinetest/partials/onlinetest-nav/onlinetest-nav.component';
import { PasswordRenewPasswordComponent } from './pages/password/password-renew-password/password-renew-password.component';
import { PasswordEnterEmailComponent } from './pages/password/password-enter-email/password-enter-email.component';
import { FormControlComponent } from './partials/ui/form-control/form-control.component';
import { FormErrorComponent } from './partials/ui/form-error/form-error.component';
import { PasswordChangeComponent } from './pages/password/password-change/password-change.component';
import { ContentNavChapterComponent } from './partials/content/content-nav-chapter/content-nav-chapter.component';
import { LangSelectorComponent } from './partials/lang-selector/lang-selector.component';
import { RatingSliderComponent } from './partials/ui/rating-slider/rating-slider.component';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { AcSelectorComponent } from './partials/ac-selector/ac-selector.component';
import { TimezoneSelectorComponent } from './partials/timezone-selector/timezone-selector.component';
import { TimezoneTimePipe } from './pipes/timezone-time.pipe';
import { InputSwitchModule } from 'primeng/inputswitch';
import { LoaderComponent } from './partials/loader/loader.component';
import { OnlinetestContainerComponent } from './pages/ac/dashboard/partials/onlinetest-container/onlinetest-container.component';
import { PreparationContainerComponent } from './pages/ac/dashboard/partials/preparation-container/preparation-container.component';
import { ParticipantContainerComponent } from './pages/ac/dashboard/partials/participant-container/participant-container.component';
import { DateContainerComponent } from './pages/ac/dashboard/partials/date-container/date-container.component';
import { CandidateSelectorComponent } from './partials/candidate-selector/candidate-selector.component';
import { ExerciseSettingsDialogComponent } from './partials/exercise-settings/exercise-settings-dialog/exercise-settings-dialog.component';
import { ExerciseSettingsGroupsComponent } from './partials/exercise-settings/exercise-settings-groups/exercise-settings-groups.component';
import { ExerciseSettingsSlotsComponent } from './partials/exercise-settings/exercise-settings-slots/exercise-settings-slots.component';
import { FormButtonsComponent } from './partials/form-buttons/form-buttons.component';
import { AvatarComponent } from './partials/avatar/avatar.component';
import { ControlsSlotComponent } from './partials/controls/controls-slot/controls-slot.component';
import { TimerComponent } from './partials/controls/timer/timer.component';
import { TimerPipe } from './pipes/timer.pipe';
import { AgendaComponent } from './partials/agenda/agenda/agenda.component';
import { AgendaExerciseSingleComponent } from './partials/agenda/exercises/agenda-exercise-single/agenda-exercise-single.component';
import { AgendaExerciseGroupComponent } from './partials/agenda/exercises/agenda-exercise-group/agenda-exercise-group.component';
import { AgendaBlockExerciseComponent } from './partials/agenda/blocks/agenda-block-exercise/agenda-block-exercise.component';
import { AgendaBlockConferenceComponent } from './partials/agenda/blocks/agenda-block-conference/agenda-block-conference.component';
import { AgendaBlockOrgComponent } from './partials/agenda/blocks/agenda-block-org/agenda-block-org.component';
import { AgendaBlockBreakComponent } from './partials/agenda/blocks/agenda-block-break/agenda-block-break.component';
import { AgendaParticipantIconsComponent } from './partials/agenda/agenda-participant-icons/agenda-participant-icons.component';
import { TabSelectorComponent } from './partials/tab-selector/tab-selector.component';
import { AgendaExerciseHeadlineComponent } from './partials/agenda/exercises/agenda-exercise-headline/agenda-exercise-headline.component';
import { AgendaExerciseSlotComponent } from './partials/agenda/exercises/agenda-exercise-slot/agenda-exercise-slot.component';
import { ContentHeaderComponent } from './partials/content/content-header/content-header.component';
import { ContentBigMessageComponent } from './partials/content/content-big-message/content-big-message.component';
import { ParticipantComponent } from './pages/ac/dashboard/partials/participant/participant.component';
import { VideoConferenceContainerComponent } from './pages/ac/dashboard/partials/video-conference-container/video-conference-container.component';
import { CloseButtonComponent } from './partials/close-button/close-button.component';
import { ContentControlsComponent } from './partials/content/content-controls/content-controls.component';
import { ConferenceControlsComponent } from './pages/ac/conference/partials/conference-controls/conference-controls.component';
import { AcMoveSelectorComponent } from './pages/ac/dashboard/partials/ac-move-selector/ac-move-selector.component';
import { FileUploadModule } from 'primeng/fileupload';
import { SplashScreenComponent } from './partials/splash-screen/splash-screen.component';
import { CoreUiModule } from 'projects/core/src/lib/modules/ui/ui.module';
import { AiReportComponent } from './pages/ac/dashboard/partials/ai-report/ai-report.component';

const DragConfig = {
  dragStartThreshold: 0,
  pointerDirectionChangeThreshold: 5,
  zIndex: 10000
};

@NgModule({
  declarations: [
    AppComponent,
    ElementComponent,
    StartComponent,
    ContentContainerComponent,
    HeadlineComponent,
    LoginComponent,
    AcComponent,
    DashboardComponent,
    NotfoundComponent,
    ByeComponent,
    NoticesComponent,
    ExerciseComponent,
    PreparationComponent,
    ConferenceComponent,
    RatingComponent,
    FileListComponent,
    BackButtonComponent,
    ReportNumberPipe,
    OnlinetestComponent,
    OnlinetestNavComponent,
    PasswordRenewPasswordComponent,
    PasswordEnterEmailComponent,
    FormControlComponent,
    FormErrorComponent,
    PasswordChangeComponent,
    ContentNavChapterComponent,
    LangSelectorComponent,
    RatingSliderComponent,
    AcSelectorComponent,
    TimezoneSelectorComponent,
    TimezoneTimePipe,
    TimerPipe,
    LoaderComponent,
    OnlinetestContainerComponent,
    PreparationContainerComponent,
    ParticipantContainerComponent,
    DateContainerComponent,
    CandidateSelectorComponent,
    ExerciseSettingsDialogComponent,
    ExerciseSettingsGroupsComponent,
    ExerciseSettingsSlotsComponent,
    FormButtonsComponent,
    AvatarComponent,
    ControlsSlotComponent,
    TimerComponent,
    AgendaComponent,
    AgendaExerciseSingleComponent,
    AgendaExerciseGroupComponent,
    AgendaBlockExerciseComponent,
    AgendaBlockConferenceComponent,
    AgendaBlockOrgComponent,
    AgendaBlockBreakComponent,
    AgendaParticipantIconsComponent,
    TabSelectorComponent,
    AgendaExerciseHeadlineComponent,
    AgendaExerciseSlotComponent,
    ContentHeaderComponent,
    ContentBigMessageComponent,
    ParticipantComponent,
    VideoConferenceContainerComponent,
    CloseButtonComponent,
    ContentControlsComponent,
    ConferenceControlsComponent,
    AcMoveSelectorComponent,
    SplashScreenComponent,
    AiReportComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    FormsModule,
    AppRoutingModule,
    HttpClientModule,
    MomentModule,
    CardModule,
    InputTextModule,
    PasswordModule,
    ButtonModule,
    TableModule,
    MessagesModule,
    MessageModule,
    ConfirmDialogModule,
    ToastModule,
    ProgressSpinnerModule,
    DropdownModule,
    SliderModule,
    DialogModule,
    TimelineModule,
    TooltipModule,
    TagModule,
    OverlayPanelModule,
    MenuModule,
    InputSwitchModule,
    FontAwesomeModule,
    EditorModule,
    TranslocoRootModule,
    NgxSliderModule,
    DragDropModule,
    OverlayPanelModule,
    FileUploadModule,
    CoreUiModule,
    CoreModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: ConfirmationService
    },
    {
      provide: MessageService
    },
    {
      provide: TINYMCE_SCRIPT_SRC,
      useValue: 'tinymce/tinymce.min.js'
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: CDK_DRAG_CONFIG,
      useValue: DragConfig
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

